import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { UiTouchscreenService } from '@ib-core/services/ui-touchscreen.service';
import { environment } from '@ib-env/environment';
import { AbstractBaseComponent } from '@ib-shared/abstract/abstract-base';
import { IDocumentOption } from '@ib-shared/interfaces/document-option.intf';
import {
  ISelectedDocument,
  SELECTED_DOC_FORM_FIELDS,
} from '@ib-shared/modules/selected-docs/selected-docs.component';
import { RegistrDocTypesService } from '@ib-shared/services/doc-types.service';
import { MediaDevicesService } from '@ib-shared/services/media-device.service';
import { INVESTOR_ACCOUNT_TYPE } from 'credebt-shared';
import { map, startWith, takeUntil } from 'rxjs';

import { SIGN_UP_FIELDS } from '../../../enums/sign-up-fields.enum';
import { SignUpFacadeService } from '../../../services/sign-up-facade.service';
import { SignUpFormHelperService } from '../../../services/sign-up-form-facade.service';

@Component({
  selector: 'ib-sign-up-step-docs',
  templateUrl: './sign-up-step-docs.component.html',
  styleUrls: ['./sign-up-step-docs.component.scss'],
})
export class SignUpStepDocsComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy
{
  @Input() form: FormGroup;
  @Input() set isActive(v: boolean) {
    this.isDevicesInited = this.uiTsS.isTouchScreen;
    if (
      !this.uiTsS.isTouchScreen &&
      v &&
      !this.isDevicesInited &&
      !this.isDevicesInitInProgress
    ) {
      this.initDesktopDevices();
    }
  }

  public docs: Array<ISelectedDocument>;
  public docsIsAddedCtrl = new FormControl(false, [Validators.requiredTrue]);
  public isDevicesInited = false;
  public isDevicesInitInProgress = false;
  public videoDevicesAvailable = false;
  public readonly SIGN_UP_FIELDS = SIGN_UP_FIELDS;
  public readonly USER_TYPE = INVESTOR_ACCOUNT_TYPE;
  public docTypes: Array<IDocumentOption>;

  get showDoubleSelection(): boolean {
    return !this.uiTsS.isTouchScreen && this.videoDevicesAvailable;
  }
  get docListAbstrFormCtrl(): AbstractControl {
    return this.form.get(SIGN_UP_FIELDS.DOCUMENTS_LIST);
  }

  @ViewChild('ppsInput', { static: false })
  ppsInput: ElementRef<HTMLInputElement>;
  @ViewChild('vatInput', { static: false })
  vatInput: ElementRef<HTMLInputElement>;

  constructor(
    private docTypesS: RegistrDocTypesService,
    public uiTsS: UiTouchscreenService,
    private mediaDevicesS: MediaDevicesService,
    public facadeS: SignUpFacadeService,
    public formS: SignUpFormHelperService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.docs = [];
    Object.values(this.formS?.docsMainFormGroup?.controls || {}).forEach(
      formGroup => {
        this.docs.push(
          formGroup.get(SELECTED_DOC_FORM_FIELDS.DOCUMENT)
            .value as ISelectedDocument,
        );
      },
    );
    this.initDocTypes();
    this.initForm();
    this.focusInput();
  }

  ngOnDestroy(): void {
    super.destroySubscriptions();
  }

  private focusInput(): void {
    setTimeout(() => {
      const input = this.formS?.isCompanyAccount
        ? this.vatInput?.nativeElement
        : this.ppsInput?.nativeElement;
      input?.focus();
    }, 300);
  }

  submitFromInput(e): void {
    e.stopPropagation();
    e.preventDefault();
    if (this.form.valid) {
      this.facadeS.setNextStep();
    } else {
      this.form.markAllAsTouched();
    }
  }

  private initDocTypes(): void {
    this.docTypes = this.getDocTypesByAccType();
    this.formS.accountTypeFormCtrl.valueChanges
      .pipe(
        startWith([]),
        map(() => this.getDocTypesByAccType()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.docTypes = this.getDocTypesByAccType();
      });
  }

  private getDocTypesByAccType(): Array<IDocumentOption> {
    const accType = this.formS.accountType;
    const docs =
      accType === INVESTOR_ACCOUNT_TYPE.PERSONAL
        ? this.docTypesS.DOCUMENTS_OPTIONS.filter(i =>
            i.accType.includes(INVESTOR_ACCOUNT_TYPE.PERSONAL),
          )
        : this.docTypesS.DOCUMENTS_OPTIONS.filter(i =>
            i.accType.includes(INVESTOR_ACCOUNT_TYPE.COMPANY),
          );
    return docs;
  }

  private initForm(): void {
    // mocks
    if (!environment.production && environment.allowSignUpUseMocks) {
      // this.form
      //   .get(SIGN_UP_FIELDS.DOC_TYPE)
      //   .setValue(INVESTOR_DOCUMENT_TYPE.UTILITY_BILL);
    }
  }

  private initDesktopDevices(): void {
    this.isDevicesInitInProgress = true;
    this.mediaDevicesS
      .selectVideoDevicesState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data.isInited) {
          this.isDevicesInited = true;
          this.isDevicesInitInProgress = false;
          this.videoDevicesAvailable = Boolean(
            data.isInited && data.devices.length && !data.error,
          );
        }
      });
  }

  removeDoc(id: string): void {
    delete this.formS.docsFormGroupsMap[id];
    this.formS.docsMainFormGroup.removeControl(id);
    this.formS.docsMainFormGroup.markAsTouched();
    this.formS.docsMainFormGroup.markAsDirty();
    this.docs = this.docs.filter(i => i.id !== id);
    this.docListAbstrFormCtrl.updateValueAndValidity();
  }

  addDocs(list: Array<ISelectedDocument>): void {
    this.docs = [...(this.docs || []), ...list].sort((a, b) => {
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
    list.forEach(doc => {
      const fg = new FormGroup({
        [SELECTED_DOC_FORM_FIELDS.DOC_NAME]: new FormControl(doc.name, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(400),
        ]),
        [SELECTED_DOC_FORM_FIELDS.DOCUMENT]: new FormControl(doc, [
          Validators.required,
        ]),
      });
      this.formS.docsFormGroupsMap[doc.id] = fg;
      this.formS.docsMainFormGroup.addControl(doc.id, fg);
    });
    this.docListAbstrFormCtrl.updateValueAndValidity();
    this.formS.docsMainFormGroup.markAsTouched();
    this.formS.docsMainFormGroup.markAsDirty();
  }
}
